/**
 * The queue the planner will walk, on screen, through the engine's own rule
 * (`applyManualOrder` — the given lists first, the rest in the default
 * order): MANUAL = the dragged sequence, FORMULA = the ranked lists best
 * first, unranked ones after in the default (biggest-first) order. Each
 * row's formula columns, and the list the next generate starts on
 * (02/11/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { applyManualOrder, type DropGenListRankingPreview, hourRangeLabel } from '@trafficpulse/shared';

import type { DraftGroupRef } from '../../../../drop-generator.types';

import { excludeHit } from '../../rules/rule-model';

interface QueueRow {
  wiserId: number;
}

export function queueOrder<T extends QueueRow>(
  byDefault: T[],
  link: Pick<DraftGroupRef, 'orderMode' | 'manualOrderJson'>,
  preview: DropGenListRankingPreview | undefined,
): T[] {
  const ids = byDefault.map((r) => r.wiserId);
  const first =
    link.orderMode === 'MANUAL'
      ? (link.manualOrderJson ?? [])
      : link.orderMode === 'FORMULA' && preview
        ? preview.lists.filter((l) => !l.unranked).map((l) => l.wiserId)
        : [];
  if (first.length === 0) return byDefault;
  const byId = new Map(byDefault.map((r) => [r.wiserId, r]));
  return applyManualOrder(ids, first).map((id) => byId.get(id)!);
}

const fmt = (n: number) => n.toLocaleString('en-US');

/** A queue row's formula columns: its score and unique visits, or why it is unranked. */
export interface FormulaCells {
  score: string | null;
  visits: string | null;
  reason: string | null;
}

export function formulaCells(wiserId: number, preview: DropGenListRankingPreview): FormulaCells {
  const l = preview.lists.find((x) => x.wiserId === wiserId);
  if (!l || l.uniqueVisits === 0) return { score: null, visits: null, reason: `No traffic in ${hourRangeLabel(preview.primaryRangeKey)}` };
  if (l.unranked) return { score: null, visits: null, reason: `Excluded — ${excludeHit(l.ruleHits)?.reason ?? `${fmt(l.uniqueVisits)} unique visits`}` };
  return { score: (l.score ?? 0).toFixed(2).replace('-', '−'), visits: fmt(l.uniqueVisits), reason: null };
}

/**
 * The list a formula group's next generate starts on (02/11/2026): the first
 * of the queue the day does not pass over (burned, resting, dry) — only once
 * the formula ranked a list (otherwise the group keeps its list).
 */
export function nextListId(ordered: QueueRow[], preview: DropGenListRankingPreview | undefined, out: number[]): number | null {
  if (!preview?.lists.some((l) => !l.unranked)) return null;
  return ordered.find((r) => !out.includes(r.wiserId))?.wiserId ?? null;
}

export type QueueMark = 'today' | 'next';

/** Today's list(s) and — one list a day — the list the next generate starts on, when another. */
export function queueMarks(ordered: QueueRow[], preview: DropGenListRankingPreview | undefined, day: { ids: number[]; out: number[] } | undefined, oneList: boolean): Map<number, QueueMark> {
  const marks = new Map<number, QueueMark>((day?.ids ?? []).map((id) => [id, 'today']));
  const next = oneList ? nextListId(ordered, preview, day?.out ?? []) : null;
  if (next !== null && !marks.has(next)) marks.set(next, 'next');
  return marks;
}
